import {FC, useEffect, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {usePlan} from "../../context";
import {SummaryCard} from "../common";
import {OrganizationDetailsColumn} from "./OrganizationDetailsColumn";
import "../common/BuilderStep.scss";
import {useAlertContext} from "../../../../../alerting";
import {useTenancies} from "../../../hooks/useTenancies";
import {CreatePlanTenantWarning} from "../../view-model";

// Handles the warning Alert that displays when multiple tenants are detected.
function useTenantWarningAlert(tenantWarning: CreatePlanTenantWarning | undefined) {
  const {t} = useTranslation();
  const [, showAlert, clearAlert] = useAlertContext();

  // Memoize for stable reference.
  const tenantIds = useMemo(
    () => tenantWarning?.tenantIds ?? [],
    [tenantWarning?.tenantIds]
  );

  const {tenants, loading: tenantsLoading} = useTenancies(tenantIds);

  useEffect(() => {
    if (!tenantWarning) {
      clearAlert();
      return;
    }

    // Names haven't arrived yet — show a loading variant of the alert so the
    // user sees something immediately rather than a blank alert area.
    if (tenantsLoading) {
      showAlert({
        type: "caution",
        title: t("create-organization.tenant-warning.title"),
        message: t("create-organization.tenant-warning.message-loading")
      });
      return () => {
        clearAlert();
      };
    }

    const nameById = new Map(tenants.map(t => [t.id, t.name]));

    const tenantNames = tenantWarning.tenantIds
      .map(id => nameById.get(id) ?? id)
      .join(", ");

    // In a potential warning Alert, this is the name that will be shown as being the one that will be used to create the new OE.
    const selectedTenantName =
      nameById.get(tenantWarning.tenantIdUsedForCreation) ?? tenantWarning.tenantIdUsedForCreation;

    showAlert({
      type: "caution",
      title: t("create-organization.tenant-warning.title"),
      message: t("create-organization.tenant-warning.message", {
        tenantId: selectedTenantName, // the tenant the plan will use
        tenantIds: tenantNames        // all competing tenants (for context)
      })
    });

    return () => {
      clearAlert();
    };
  }, [clearAlert, tenantWarning, showAlert, t, tenants, tenantsLoading]);
}

export const ReviewPlanStep: FC = () => {
  const {plan} = usePlan();

  useTenantWarningAlert(plan.tenantWarning);

  return (
    <section
      id={"review-plan-step"}
      data-testid={"review-plan-step"}
      className={"organization-builder-step review-plan-step"}>
      <section
        id={"summary-section"}
        data-testid={"summary-section"}
        className={"summary-section"}>
        <SummaryCard
          baseLabelKey={"create-organization.plan-summary"}
          qualifier={"creating"}
          count={1}
        />
        <SummaryCard
          baseLabelKey={"create-organization.plan-summary"}
          qualifier={plan.newEntity.tenantId ? "existing-tenant" : "new-tenant"}
          count={1}
          name={plan.source.hierarchy?.custName ?? ""}
        />
      </section>
      <section
        id={"organization-section"}
        data-testid={"organization-section"}
        className={"organization-section"}>
        <OrganizationDetailsColumn plan={plan} />
      </section>
    </section>
  );
};
